import React, { useCallback, useEffect, useState } from "react";
import { axiosClient } from "../api/proxy/AxiosClient";

import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid2,
  Collapse,
  Paper,
  TableContainer,
} from "@mui/material";
import { useServices } from "../services/ServiceContext";

const StudentSummary = ({ user }) => {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { lesson } = useServices();
  const [activeCurrency, setActiveCurrency] = useState(null);
  const currentBalance = {};
  const [activeTeacher, setActiveTeacher] = useState(null);

  const handleCurrencyClick = (ccy) => {
    setActiveCurrency(prev => (prev === ccy ? null : ccy));
  };

  const handleTeacherClick = (teacher) => {
    setActiveTeacher(prev => (prev?.uid === teacher.uid ? null : teacher));
  };

  const isCurrencySelected = (ccy) => activeCurrency === ccy;

  const handleBalanceClick = (ccy, teacher) => {
    if (ccy !== activeCurrency) {
      setActiveCurrency(ccy);
    }
    if (teacher.uid !== activeTeacher?.uid) {
      setActiveTeacher(teacher);
    }
  };

  const getLessonsGroupLength = (group) => {
    const count = group.lessons.filter(l => !activeTeacher || l.teacher.uid === activeTeacher?.uid).length;
    return count;
  }

  const [summary, setSummary] = useState({
    lessons: [],
    lessonGroupsPlanning: {
      ongoing: [],
      upcoming: [],
      requiresAction: [],
      waitingTheOtherSide: [],
      completed: [],
      cancelled: [],
      deleted: [],  
    },
    lessonGroupsBilling : {
      unclear: [],
      outdated: [],
      billed: [],
      paid: [],
    },
    balanceRecords: {},
    balanceTable: {},
    totalsByCurrency: {},
    totalsByTeacher: {},
  });

  const viewEditLesson = async (l) => {
    await lesson.openEditLessonForStudent(l, summary.lessons, () => {
      //setActiveLesson(prev => (prev?.uid === l.uid ? null : l))
    });
  };

  const lessonGroupsPlanning = [
    { key: "ongoing", label: "Ongoing", color: "primary", lessons: summary.lessonGroupsPlanning.ongoing },
    { key: "upcoming", label: "Upcoming", color: "info", lessons: summary.lessonGroupsPlanning.upcoming },
    { key: "requiresAction", label: "Requires action", color: "warning", lessons: summary.lessonGroupsPlanning.requiresAction },
    { key: "waitingTheOtherSide", label: "Waiting the other side", color: "info", lessons: summary.lessonGroupsPlanning.waitingTheOtherSide },
    { key: "completed", label: "Completed", color: "success", lessons: summary.lessonGroupsPlanning.completed },
    { key: "cancelled", label: "Cancelled", color: "default", lessons: summary.lessonGroupsPlanning.cancelled },
  ];
  const [activeGroupKey, setActiveGroupKey] = useState(null);
  const handleGroupClick = (key) => {
    setActiveGroupKey(prev => (prev === key ? null : key));
  };
  const activeGroupPlanning = lessonGroupsPlanning.find(g => g.key === activeGroupKey);

  const lessonGroupsBilling = [
    { key: "unclear", label: "Unclear", color: "primary", lessons: summary.lessonGroupsBilling.unclear },
    { key: "outdated", label: "Outdated", color: "info", lessons: summary.lessonGroupsBilling.outdated },
    { key: "billed", label: "Should be paid", color: "warning", lessons: summary.lessonGroupsBilling.billed },
    { key: "paid", label: "Paid", color: "info", lessons: summary.lessonGroupsBilling.paid },
  ];
  const activeGroupBilling = lessonGroupsBilling.find(g => g.key === activeGroupKey);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [lessonsResponse, paymentsResponse] = await Promise.all([
        axiosClient.get(`/lessons?studentUid=${user.uid}`),
        axiosClient.get(`/payments`),
      ]);

      const lessons = (lessonsResponse?.data || []).sort(
        (a, b) => new Date(b.startDttm) - new Date(a.startDttm)
      );

      const payments = paymentsResponse?.data || [];

      const lessonGroupsPlanning = {
        ongoing: lessons.filter(l => l.status === "ongoing"),
        upcoming: lessons.filter(l =>
          ["submitted", "confirmed"].includes(l.status)
        ),
        requiresAction: lessons.filter(l =>
          ["proposed", "moved"].includes(l.status)
        ),
        waitingTheOtherSide: lessons.filter(l =>
          ["rescheduled", "requested"].includes(l.status)
        ), 
        completed: lessons.filter(l =>
          ["finished", "billed"].includes(l.status)
        ),
        cancelled: lessons.filter(l =>
          ["rejected", "cancelled"].includes(l.status)
        ),
        deleted: lessons.filter(l => 
          l.status === "deleted"
        ),
      };

      const lessonGroupsBilling = {
        unclear: lessons.filter(l => ["proposed", "moved", "rescheduled", "requested"].includes(l.status)),
        outdated: lessons.filter(l => ["proposed", "moved", "rescheduled", "requested"].includes(l.status))
                         .filter(l => new Date(l.endDttm) < new Date()),
        billed: lessons.filter(l =>["finished", "billed", "ongoing"].includes(l.status))
                       .filter(l => l.transactions.reduce((sum, tx) => sum + (tx.type === "payment" ? tx.amount : 0), 0) < (l.fee * l.students.length)),
        paid: lessons.filter(l => l.transactions.length),
      };

      let balanceTable = {
        teachers: {},
        currencies: new Set(),
      };

      balanceTable = lessons.reduce((acc, lesson) => {
        const { teacher: creditor, ccy } = lesson;

        if (!acc.teachers[creditor.uid]) {
          acc.teachers[creditor.uid] = {
            uid: creditor.uid,
            name: `${creditor.firstName} ${creditor.lastName}`,
            balances: {},
          };
        }

        if (["billed", "finished", "ongoing"].includes(lesson.status)) {
          acc.teachers[creditor.uid].balances[ccy] =
          (acc.teachers[creditor.uid].balances[ccy] || 0) - lesson.fee;
    
          acc.currencies.add(ccy);
        }

        return acc;
      }, balanceTable );

      balanceTable = payments.reduce(
        (acc, tx) => {
          const { uid, firstName, lastName } = tx.creditor;
          const ccy = tx.ccy;
      
          if (!acc.teachers[uid]) {
            acc.teachers[uid] = {
              uid,
              name: `${firstName} ${lastName}`,
              balances: {},
            };
          }
      
          acc.teachers[uid].balances[ccy] =
            (acc.teachers[uid].balances[ccy] || 0) +
            (tx.type === "deposit" ? tx.amount : 0);
      
          acc.currencies.add(ccy);
      
          return acc;
        }, balanceTable );

      const balanceRecords = {};

      lessons.forEach(l => {
        balanceRecords[l.uid] = {
          id: l.uid,
          lesson: l,
          student: l.s,
          fee: l.fee || 0,
          paid: 0,
          shouldBePaid: ["finished", "billed"].includes(l.status),
          futurePayment: ["confirmed", "submitted", "ongoing"].includes(l.status),
          date: new Date(l.startDttm),
          payments: [],
          currency: l.ccy,
        };
      });

      payments
        // .filter(tx => tx.lesson?.uid)
        .forEach(tx => {
          if (tx.type === "payment") {
            const br = balanceRecords[tx.lesson.uid];
            if (!br) return;
            br.payments.push(tx);
            br.paid += tx.amount;
          } else {
            balanceRecords[tx.id] = {
              id: tx.id,
              payments: [tx],
              currency: tx.ccy,
              date: new Date(tx.created_at),
              deposited: tx.amount,
              fee: 0,
              paid: 0
            };
          }
        });

      const totalsByCurrency = Object.values(balanceRecords)
        .filter(br => br.shouldBePaid || br.deposited || br.futurePayment)
        .reduce((acc, br) => {
          acc[br.currency] = acc[br.currency] || { totalFee: 0, totalPaid: 0, totalDeposited: 0 };
          acc[br.currency].totalFee += br.fee || 0;
          acc[br.currency].totalPaid += br.paid || 0;
          acc[br.currency].totalDeposited += br.deposited || 0;
          return acc;
        }, {});

        const totalsByTeacher = Object.values(balanceRecords)
        .filter(br => br.shouldBePaid || br.deposited || br.futurePayment)
        .reduce((acc, br) => {
            const teacherUid = br.lesson?.teacher?.uid || br.payments[0]?.creditor.uid;
            acc[teacherUid] = acc[teacherUid] || {};
            acc[teacherUid][br.currency] = acc[teacherUid][br.currency] || { totalFee: 0, totalPaid: 0, totalDeposited: 0 };
            acc[teacherUid][br.currency].totalFee += br.fee || 0;
            acc[teacherUid][br.currency].totalPaid += br.paid || 0;
            acc[teacherUid][br.currency].totalDeposited += br.deposited || 0;
          return acc;
        }, {});

      setSummary({
        lessons,
        lessonGroupsPlanning: lessonGroupsPlanning,
        lessonGroupsBilling: lessonGroupsBilling,
        balanceRecords,
        balanceTable: balanceTable,
        totalsByCurrency,
        totalsByTeacher,
      });
    } catch (e) {
      console.error(e);
      setError("Coudn't load student summary. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [user.uid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ===================== RENDER ===================== */

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">

      {/* -------- LESSONS OVERVIEW -------- */}
      <Card sx={{ mb: 4 }}>
  <CardContent>
    <Typography variant="h6" gutterBottom>
    {`Lessons planning ` + (activeTeacher ? ` - ${activeTeacher.name}` : '')}
    </Typography>

    {/* KPI-блоки */}
    <Grid2 container spacing={2} mb={2}>
      {lessonGroupsPlanning.map(group => (
        <Grid2 key={group.key}>
          <Card
            variant="outlined"
            onClick={() => handleGroupClick(group.key)}
            sx={{
              cursor: "pointer",
              borderColor: activeGroupKey === group.key ? "primary.main" : undefined,
              bgcolor: activeGroupKey === group.key ? "action.hover" : undefined,
            }}
          >
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {group.label}
              </Typography>
              <Chip
               label={getLessonsGroupLength(group)}
               color={getLessonsGroupLength(group) > 0 ? group.color : "gray"}
              />
            </CardContent>
          </Card>
        </Grid2>
      ))}
    </Grid2>

    <Collapse in={Boolean(activeGroupPlanning)} timeout="auto" unmountOnExit>
      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          <Typography variant="subtitle1" sx={{ p: 2 }}>
            {activeGroupPlanning?.label} lessons
          </Typography>

          {activeGroupPlanning?.lessons.filter(l => !activeTeacher || l.teacher.uid === activeTeacher?.uid).length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, pb: 2, fontStyle: "italic" }}
            >
              No lessons in this group
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lesson</TableCell>
                    <TableCell>Teacher</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {activeGroupPlanning?.lessons
                  .filter(l => !activeTeacher || l.teacher.uid === activeTeacher?.uid)
                  .map(lesson => (
                    <TableRow
                      key={lesson.uid}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => viewEditLesson(lesson)}
                    >
                      <TableCell>{lesson.name}</TableCell>

                      <TableCell>
                        {`${lesson.teacher?.firstName} ${lesson.teacher?.lastName}`}
                      </TableCell>

                      <TableCell>
                        {new Date(lesson.startDttm).toLocaleDateString()}
                      </TableCell>

                      <TableCell>
                        {new Date(lesson.startDttm).toLocaleTimeString()} -
                        {" "}
                        {new Date(lesson.endDttm).toLocaleTimeString()}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={lesson.status}
                          size="small"
                          color={
                            lesson.status === "COMPLETED"
                              ? "success"
                              : lesson.status === "CANCELLED"
                              ? "default"
                              : "info"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Collapse>

    <Typography variant="h6" gutterBottom>
      {'Lessons billing' + (activeTeacher ? ` - ${activeTeacher.name}` : '')}
    </Typography>

    {/* KPI-блоки */}
    <Grid2 container spacing={2} mb={2}>
      {lessonGroupsBilling.map(group => (
        <Grid2 key={group.key}>
          <Card
            variant="outlined"
            onClick={() => handleGroupClick(group.key)}
            sx={{
              cursor: "pointer",
              borderColor: activeGroupKey === group.key ? "primary.main" : undefined,
              bgcolor: activeGroupKey === group.key ? "action.hover" : undefined,
            }}
          >
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {group.label}
              </Typography>
              <Chip
               label={getLessonsGroupLength(group)}
               color={getLessonsGroupLength(group) > 0 ? group.color : "gray"}
              />
            </CardContent>
          </Card>
        </Grid2>
      ))}
    </Grid2>

    {/* Детальный блок */}
    <Collapse in={Boolean(activeGroupBilling)} timeout="auto" unmountOnExit>
      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          <Typography variant="subtitle1" sx={{ p: 2 }}>
            {activeGroupBilling?.label} lessons
          </Typography>

          {activeGroupBilling?.lessons.filter(l => !activeTeacher || l.teacher.uid === activeTeacher.uid).length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, pb: 2, fontStyle: "italic" }}
            >
              No lessons in this group
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lesson</TableCell>
                    <TableCell>Teacher</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {activeGroupBilling?.lessons
                  .filter(l => !activeTeacher || l.teacher.uid === activeTeacher.uid)
                  .map(lesson => (
                    <TableRow
                      key={lesson.uid}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => viewEditLesson(lesson)}
                    >
                      <TableCell>{lesson.name}</TableCell>

                      <TableCell>
                        {`${lesson.teacher?.firstName} ${lesson.teacher?.lastName}`}
                      </TableCell>

                      <TableCell>
                        {new Date(lesson.startDttm).toLocaleDateString()}
                      </TableCell>

                      <TableCell>
                        {new Date(lesson.startDttm).toLocaleTimeString()} -
                        {" "}
                        {new Date(lesson.endDttm).toLocaleTimeString()}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={lesson.status}
                          size="small"
                          color={
                            lesson.status === "COMPLETED"
                              ? "success"
                              : lesson.status === "CANCELLED"
                              ? "default"
                              : "info"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Collapse>




  </CardContent>
</Card>



      {/* -------- BALANCE -------- */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Balance by teacher
          </Typography>

          {Object.values(summary.balanceTable.teachers).length === 0 ? (
            <Typography color="text.secondary">N/A</Typography>
          ) : (
            <TableContainer
              sx={{
                overflowX: 'hidden',
                overflowY: 'hidden',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Teacher</TableCell>
                    {Array.from(summary.balanceTable.currencies).sort().map(ccy => (
                      <TableCell
                        key={ccy}
                        align="right"
                        onClick={() => handleCurrencyClick(ccy)}
                        sx={{
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'background-color 0.7s ease, transform 0.3s ease',
                          backgroundColor: activeCurrency === ccy ? "#d0f0d0" : "white",
                          '&:hover': {
                            backgroundColor: 'rgba(103, 211, 88, 0.66)',
                            transform: 'scale(1.2)',
                          },
                        }}
                      >
                        {ccy.toUpperCase()}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {Object.values(summary.balanceTable.teachers).map(teacher => (
                    <TableRow
                      key={teacher.uid}
                    >
                      <TableCell
                        onClick={() => handleTeacherClick(teacher)}
                        sx={{
                          transition: 'background-color 0.7s ease, transform 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(103, 211, 88, 0.66)',
                            transform: 'translateX(15px)',
                          },
                          backgroundColor: activeTeacher === teacher ? "#d0f0d0" : "white",
                        }}
                      >
                        {teacher.name}
                      </TableCell>

                      {Array.from(summary.balanceTable.currencies).sort().map(ccy => {
                        const amount = teacher.balances[ccy] || 0;

                        return (
                          <TableCell
                            key={ccy + teacher.uid}
                            align="right"
                            sx={{
                              color:
                                amount > 0
                                  ? "success.main"
                                  : amount < 0
                                  ? "error.main"
                                  : "text.secondary",
                              fontWeight: amount !== 0 ? 500 : 400,
                              transition: 'background-color 0.7s ease, transform 0.3s ease',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'rgba(103, 211, 88, 0.66)',
                                transform: 'scale(1.1)',
                              },
                              backgroundColor: activeCurrency === ccy || activeTeacher === teacher ? "#d0f0d0" : "white",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBalanceClick(ccy, teacher);
                            }}
                          >
                            {amount.toFixed(2)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>


      {/* -------- TOTALS -------- */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Totals
          </Typography>

          {Object.keys(summary.totalsByCurrency).length === 0 || Object.keys(summary.totalsByTeacher).length === 0 ? (
            <Typography color="text.secondary">N/A</Typography>
          ) : (
            <Grid2 container spacing={3}>
              {Object.entries(
                activeTeacher
                  ? summary.totalsByTeacher[activeTeacher.uid] || {}
                  : summary.totalsByCurrency
              ).map(
                ([ccy, totals]) => {
                  const outstanding = totals.totalDeposited - totals.totalFee;

                  return (  
                    <Grid2 key={ccy}>
                      <Card
                        variant="outlined"
                        onClick={() => handleCurrencyClick(ccy)}
                         sx={{
                          cursor: "pointer",
                          backgroundColor: isCurrencySelected(ccy) ? "#d0f0d0" : "white",
                          "&:hover": {
                            backgroundColor: isCurrencySelected(ccy) ? "#d0f0d0" : "#e6f2ff",
                          },
                          position: "relative",
                        }}>
                        <CardContent>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ cursor: "pointer" }}
                        >
                          {ccy.toUpperCase()}
                        </Typography>

                          <Divider sx={{ mb: 2 }} />

                          <Typography variant="body2">
                            Total fee: {totals.totalFee.toFixed(2)}
                          </Typography>
                          <Typography variant="body2">
                            Paid: {totals.totalPaid.toFixed(2)}
                          </Typography>
                          <Typography variant="body2">
                            Deposited: {totals.totalDeposited.toFixed(2)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={
                              outstanding >= 0 ? "success.main" : "error.main"
                            }
                          >
                            Outstanding: {outstanding.toFixed(2)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid2>
                  );
                }
              )}
            </Grid2>
          )}
        </CardContent>
        <Collapse in={Boolean(activeCurrency)} timeout="auto" unmountOnExit>
          <Card sx={{ mb: 4 }} variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Transactions - {activeCurrency?.toUpperCase()}{activeTeacher ? ` - ${activeTeacher.name}` : ''}
              </Typography>

              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Lesson / Transaction date</TableCell>
                      {activeTeacher ? <></> : <TableCell>Teacher / Debtor</TableCell>}
                      <TableCell>Lesson status</TableCell>
                      <TableCell align="right">Fee</TableCell>
                      <TableCell align="right">Deposited</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Current Balance</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell>Transactions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {Object.values(summary.balanceRecords)
                      .filter(br => !activeTeacher || br.payments[0]?.creditor.uid === activeTeacher?.uid || br.lesson?.teacher?.uid === activeTeacher?.uid)
                      .filter(br => br.currency === activeCurrency && (br.shouldBePaid || br.deposited || br.futurePayment))
                      .sort((a, b) => a.date - b.date)
                      .map(br => {
                        br.remaining = (br.fee - br.paid) || 0;
                        let ccyBalance = currentBalance[br.currency];
                        if (!ccyBalance) {
                          ccyBalance = { value: (br.deposited || 0) - (br.paid || br.fee || 0) };
                          currentBalance[br.currency] = ccyBalance;
                        } else {
                          ccyBalance.value += ((br.deposited || 0) - (br.paid || br.fee || 0));
                        }
                        br.balanceValue = ccyBalance.value;
                        return br;
                       })
                      .sort((a, b) => { 
                        const getDate = br => br.lesson ? new Date(br.lesson.startDttm) : new Date(br.payments[0]?.created_at);
                        const dateA = getDate(a);
                        const dateB = getDate(b);
                        return dateB - dateA;
                      }).map(br => {
                        return (
                          <TableRow key={br.lesson?.uid || br.payments[0]?.id} hover>
                            <TableCell>
                              {!!br.lesson ? new Date(br.lesson?.startDttm).toLocaleDateString() : new Date(br.payments[0]?.created_at).toLocaleDateString()}
                            </TableCell>

                            {
                              activeTeacher ? <></> :
                              <TableCell>
                                {br.lesson ? `${br.lesson.teacher.firstName + ' ' + br.lesson.teacher.lastName}` : `${br.payments[0]?.debtor.firstName} ${br.payments[0]?.debtor.lastName}`}
                              </TableCell>
                            }

                            <TableCell>
                              {br.lesson?.status}
                            </TableCell>

                            <TableCell align="right">
                              {br.shouldBePaid && br.fee?.toFixed(2)}
                            </TableCell>

                            <TableCell
                             align="right"
                             sx={{
                               color: "success.main",
                             }}
                            >
                              {br.deposited?.toFixed(2)}
                            </TableCell>

                            <TableCell align="right">
                              {br.shouldBePaid && br.paid?.toFixed(2)}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  br.balanceValue < 0
                                    ? "error.main"
                                    : "success.main",
                              }}
                            >
                              {br.balanceValue.toFixed(2)}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  br.remaining > 0
                                    ? "error.main"
                                    : "success.main",
                              }}
                            >
                              {br.shouldBePaid && br.remaining.toFixed(2)}
                            </TableCell>

                            <TableCell>
                              {br.payments.length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No transactions
                                </Typography>
                              ) : (
                                br.payments.map(tx => (
                                  <Box key={tx.id} mb={0.5}>
                                    <Typography variant="body2">
                                      {tx.type}
                                      {" - "}
                                      {tx.amount.toFixed(2)}
                                    </Typography>
                                  </Box>
                                ))
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Collapse>

      </Card>
    </Container>
  );
};

export default StudentSummary;
